<template>
  <div ref="localInstance">
    <label class="label button-selector-label">
      {{label}}
      <b-icon pack="fas" :icon="required ? 'star' : ' '" class="fa-ss" style="order:1"></b-icon>
      <a @click="onShowAllOptions" v-if="showAllToggle && isRolledUp === false" class="has-text-primary toggle" style="order:2">{{showAllOptions === true ? 'dept. only' : 'show all'}}</a>
      <span style="order:2; margin-right:10px;" v-if="showAllOptions && showGroupToggle && isRolledUp === false" >|</span>
      <a @click="onShowGroupOptions" v-if="showAllOptions && showGroupToggle && isRolledUp === false" class="has-text-primary toggle" style="order:3">{{showGroupOptions === true ? 'my dealer' : 'all dealers'}}</a>
      <!-- The name is plain text; the contact part is a slot so a caller that needs the number to be
           actionable - the department number, which is click-to-call - can hand in a component
           instead of a string. The default keeps the v-html markup that colours the dialable digits
           while leaving the separator and the word EXT plain; additionalDisplayOptions escapes every
           field it interpolates, so nothing unescaped reaches the DOM. -->
      <span class="selected-value-display is-pulled-right clickable" :class="{'has-text-link': isShowingEmptyText || selectedAvailable(selectedValue), 'has-text-danger': !isShowingEmptyText && !selectedAvailable(selectedValue)}" v-tooltip="getSelectedNameTitle(selectedValue)" @click="isRolledUp = false" style="order:4">
        <template v-if="selectedValue">
          <span class="selected-value-name">{{ selectedValue[labelName] }}</span>
          <slot name="selected-extras" :item="selectedValue">
            <span v-html="additionalDisplayOptions(selectedValue, 'value')"></span>
          </slot>
        </template>
        <template v-else>{{ isShowingEmptyText ? emptyText : '' }}</template>
      </span>
      <i v-tooltip="'Click to expand options'" @click="isRolledUp = !isRolledUp" v-if="isRolledUp === true" class="fal fa-angle-double-down fa-lg clickable" style="order:5;margin-left: auto;"></i>
      <i v-tooltip="'Click to expand options'" @click="isRolledUp = !isRolledUp" v-if="isValueSelected && isRolledUp === false" class="fal fa-angle-double-up fa-lg clickable" style="order:5;margin-left: auto;"></i>
    </label>
    <input class="input" ref="filter" type="text" v-model.trim="filter" style="margin-bottom:3px;" :placeholder="filterPlaceholder" v-if="showFilterControls" v-on:keyup.enter="onSelectFirst"/>
    <div class="control" :class="optionsDisplayClasses">
      <div class="buttons has-addons instance is-marginless">
        <input ref="control" :data-vv-scope="scope" type="text" :name="instanceName" v-model="getValue" :data-vv-as="this.validateAs" v-validate="{'required': required}" style="width:0px;height:0px;margin:0px;padding:0px;border:0px;"/>
        <button :tabindex="tabIndex" type=button :class="{'boption': true, 'button': true, 'is-info': isSelected(selectedValue, availableValue)}" v-for="availableValue in activeList" :key="availableValue.id" @click="onAvailableValueSelected(availableValue)" :disabled="enabled === false || isItemDisabled(availableValue)" v-tooltip="getDisplayTitle(availableValue)">
          <span class="icon is-small" v-if="availableValue.ico">
            <i v-bind:class="[availableValue.ico]"></i>
          </span>
          <span>{{getDisplayName(availableValue)}}</span>
        </button>
      </div>
      <span v-show="errors.has(validationName)" class="help is-danger"><i v-show="errors.has(validationName)" class="fas fa-exclamation-triangle"></i> {{ errors.first(validationName) }}</span>
    </div>
  </div>
</template>

<script>
import { inject } from 'vue'

import {VTooltip} from 'v-tooltip'
export default {
  name: 'CompanyDirectoryButtonListSelector',
  directives: {tooltip: VTooltip},
  props: {
    value: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: 'Item'
    },
    validateAs: {
      type: String,
      default: 'Item'
    },
    availableValues: {
      type: Array,
      default: () => [{id: 1, name: 'Sample 1'}, {id: 2, name: 'Sample 2'}]
    },
    required: {
      type: Boolean,
      default: false
    },
    enabled: {
      type: Boolean,
      default: true
    },
    activeValues: {
      type: Array,
      default: () => []
    },
    hideInactiveValues: {
      type: Boolean,
      default: false
    },
    scope: {
      type: String,
      default: null
    },
    tabIndex: {
      type: Number,
      default: 0
    },
    labelName: {
      type: String,
      default: 'name'
    },
    additionalDisplayOptions: {
      type: Function,
      default: () => ''
    },
    buildTitle: {
      type: Function,
      default: (x) => x['name'] ? x['name'] : ''
    },
    // Shown in the selected-value slot when nothing is selected AND there is
    // nothing to select. Owners pass '' to suppress it entirely.
    emptyText: {
      type: String,
      default: ''
    },
    filterFunction: {
      type: Function,
      default: null
    },
    useFilterPlaceholder: {
      type: String,
      default: ''
    },
    showAllToggle: {
      type: Boolean,
      default: false
    },
    showGroupToggle: {
      type: Boolean,
      default: false
    },
    showFilter: {
      type: Boolean,
      default: false
    },
    collapseOnSelection: {
      type: Boolean,
      default: true
    },
    showAll: {
      type: Boolean,
      default: false
    },
    selectedIsAvailable: {
      type: Function,
      default: () => true
    },
    useValidator: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      selectedValue: this.value,
      instanceName: 'cdbls-' + this._uid,
      filter: '',
      showAllOptions: this.showAll,
      showGroupOptions: false,
      isRolledUp: false,
      activeList: []
    }
  },
  computed: {
    validator() {
      return this.useValidator || this.$validator
    },
    isShowingEmptyText: function () {
      return !this.selectedValue && this.emptyText !== '' && this.availableValues.length === 0
    },
    optionsDisplayClasses: function () {
      var result = this.isRolledUp ? 'options-collapsed' : 'options-visible'
      let classToAttach = {}
      classToAttach[result] = true
      return classToAttach
    },
    showFilterControls: function () {
      if (!this.isSearchable) {
        return false
      }

      return this.showFilter || !this.showFilter && !this.isRolledUp
    },
    isSearchable: function () {
      return this.availableValues.length > 8
    },
    filterPlaceholder: function () {
      if (this.useFilterPlaceholder !== '') {
        return this.useFilterPlaceholder
      }
      return 'Type to filter the \'' + this.label + '\' list of options'
    },
    isValueSelected: function () {
      return this.selectedValue && this.selectedValue.id
    },
    getValue: function () {
      return this.selectedValue ? this.selectedValue.id : ''
    },
    validationName: function () {
      return this.scope ? this.scope + '.' + this.instanceName : this.instanceName
    }
  },
  watch: {
    value: function (newValue) {
      this.selectedValue = newValue
    },
    selectedValue: function (newValue) {
      if (this.collapseOnSelection) {
        this.isRolledUp = newValue !== undefined && newValue !== null
        this.filter = ''
      }
    },
    availableValues: function () {
      this.onFilter(this.filter)
    },
    activeValues: function () {
      // eslint-disable-next-line no-self-assign
      this.availableValues = this.availableValues
    },
    filterFunction: function () {
      this.onFilter(this.filter)
    },
    filter: function (newValue) {
      this.onFilter(newValue)
    }
  },
  methods: {
    onFilter: function (filterValue) {
      if (filterValue && filterValue !== '') {
        this.isRolledUp = false
        if (this.filterFunction) {
          this.activeList = this.availableValues.filter((x) => this.filterFunction(x, filterValue))
        } else {
          this.activeList = this.availableValues.filter((x) => x[this.labelName].toLowerCase().indexOf(filterValue.toLowerCase()) >= 0)
        }
        if (this.activeList && this.activeList.length < 1) {
          this.$nextTick(() => {
            if (this.showAllToggle && !this.showAllOptions) this.onShowAllOptions()
            if (this.showGroupToggle && !this.showGroupOptions) this.onShowGroupOptions()
          })
        }
      } else {
        this.activeList = this.availableValues
      }
    },
    focus: function () {
      this.$nextTick(() => {
        if (this.$refs.filter) {
          this.$refs.filter.focus()
        }
      })
    },
    onSelectFirst: function () {
      let parent = this.$refs.localInstance
      let els = parent.getElementsByClassName('boption')
      if (els && els.length > 0) {
        els[0].click()
      }
    },
    onShowAllOptions: function () {
      this.showAllOptions = !this.showAllOptions
      this.$emit('on-show-all-options', this.showAllOptions)
      this.$nextTick(() => {
        if (this.$refs.filter) {
          this.$refs.filter.focus()
        }
      })
    },
    onShowGroupOptions: function () {
      this.showGroupOptions = !this.showGroupOptions
      this.$emit('on-show-group-options', this.showGroupOptions)
      this.$nextTick(() => {
        if (this.$refs.filter) {
          this.$refs.filter.focus()
        }
      })
    },
    // Public: lets an owner re-open the option list after it has rolled up.
    // isRolledUp is internal state the user also drives, so a prop would need
    // the parent to mirror every manual toggle just to flip it back.
    expandOptions: function () {
      this.isRolledUp = false
    },
    getDisplayTitle: function (item) {
      return this.buildTitle(item)
    },
    getDisplayName: function (item) {
      return item[this.labelName]
    },
    getSelectedNameTitle: function () {
      if (this.selectedValue) {
        let extras = this.additionalDisplayOptions(this.selectedValue, 'title')
        if (extras !== '') {
          extras = extras + 'Click to select new option'
        } else {
          extras = 'Click to select new option'
        }
        return extras
      }
      return ''
    },
    onAvailableValueSelected: function (value) {
      if (this.enabled) {
        if (this.selectedValue) {
          if (value && this.selectedValue[this.labelName] === value[[this.labelName]]) {
            this.selectedValue = null
          } else {
            this.selectedValue = value
          }
        } else {
          this.selectedValue = value
        }
        this.$emit('input', this.selectedValue)
        this.$emit('on-change', this.selectedValue)
        let form = this.$refs.control.closest('form')
        if (form) {
          var event = new Event('change')
          form.dispatchEvent(event)
        }
      }
    },
    isItemDisabled: function (item) {
      if (this.activeValues.length === 0) {
        return false
      }
      return (!this.activeValues.find((x) => x.id === item.id))
    },
    isSelected: function (selectionValue, allowedValue) {
      if (!selectionValue) {
        return false
      }
      return allowedValue.id === selectionValue.id
    },
    selectedAvailable: function (selected) {
      return this.selectedIsAvailable(selected)
    }
  },
  mounted () {
    this.isRolledUp = this.value !== undefined && this.value !== null
    this.onFilter(this.filter)
  },
  created: function () {
    if (this.useValidator) {
      this.$validator = this.useValidator
    } else {
      this.$validator = inject('$validator')
    }
  }
}
</script>

<style scoped>
  .instance .button {
    height:3.3em !important;
  }

  .button-selector-label {
    display:flex;
    align-items:flex-end;
    height:22px;
    margin-bottom:0.75em;
  }

  .selected-value-display {
    margin-top: 0.5em;
  }

  /* Blue means dialable, so only the number and extension are blue - the name is plain black.
     Scoped to has-text-link so an unavailable selection still shows red. */
  .has-text-link .selected-value-name {
    color: #0a0a0a;
  }

  .toggle {
    margin-right:10px;
  }

  .options-visible {
    -webkit-transition: all .3s ease-in-out;
    transition: all .3s ease-in-out;
    max-height: 9999px;
    visibility: visible;
    opacity: 1;
  }

  .options-collapsed {
    -webkit-transition: all .3s ease-in-out;
    transition: all .3s ease-in-out;
    max-height: 0;
    visibility: hidden;
    opacity: 0;
  }
</style>
